import { getActivityForSiteLocationMember } from '@gym-graphql/queries';
import AccessLog, { accessLogPageSize } from '@gym-organisms/AccessLog/AccessLog';
import { Activity } from '@gym-particles/types/ActivityFeed';
import { UserRole } from '@gym-particles/types/User';
import { useAppSelector } from '@gym-redux/store';
import { GymActivity, GymActivityResponse } from '@gym-src/API';
import { API } from 'aws-amplify';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);

const accessDoorStageId = 3;
const allEventTypes = 'all';

const toGymTime = (date: string, timeZone?: string) =>
  timeZone ? dayjs.utc(date).tz(timeZone) : dayjs.utc(date).local();

const toActivity = (activity: GymActivity, timeZone?: string): Activity => ({
  memberEventId: activity.memberEventId || 0,
  memberId: activity.memberId || 0,
  memberEmail: activity.memberEmail || '',
  eventId: activity.eventId || 0,
  accessCardUID: activity.accessCardUID || undefined,
  eventDescription: activity.eventDescription || '',
  eventType: activity.eventType || '',
  stageId: activity.stageId || 0,
  stage: activity.stage || '',
  createdTime: activity.createdTime
    ? toGymTime(activity.createdTime, timeZone).format('hh:mm A').toString()
    : '',
  isAutoAssigned: activity.isAutoAssigned || false,
  memberEventStatusTypeId: activity.memberEventStatusTypeId || 0,
  memberEventStatusType: activity.memberEventStatusType || '',
  firstName: activity.firstName || '',
  lastName: activity.lastName || '',
  siteLocationId: activity.siteLocationId || 0,
  locationName: activity.locationName || '',
  lastModifiedBy: activity.lastModifiedBy || 0,
  lastModifiedDate: activity.lastModifiedDate || '',
  activityDate: activity.createdTime
    ? toGymTime(activity.createdTime, timeZone).format('MM DD YYYY').toString()
    : '',
  assignedUserId: activity.assignedUserId || 0,
  assignmentHistoryId: activity.assignmentHistoryId || undefined,
  inquiryDescription: activity.inquiryDescription || '',
  memberLocationId: activity.memberLocationId || 0,
  informationReceivedEvent: activity.informationReceivedEvent || false,
  language: activity.language || 'en',
  userId: activity.userId || 0,
  en: activity.en || '',
  nor: activity.nor || '',
  nl: activity.nl || '',
  locationAccessPointId: activity.locationAccessPointId || undefined,
  locationAccessPointName: activity.locationAccessPointName || undefined
});

const parseActivities = (
  items: Array<GymActivity | null> | null | undefined,
  timeZone?: string
): Activity[] =>
  (items || []).flatMap((activity) => (activity ? [toActivity(activity, timeZone)] : []));

const MemberDetailsActivity = ({ gymId, memberId }: MemberDetailsActivityProps) => {
  const userId = useAppSelector((state) => state.user.userId);
  const userRole = useAppSelector((state) => state.user.userRole);
  const currentGym = useAppSelector((state) =>
    state.gyms.items
      .find((gymChain) => gymChain.items.some((gym) => gym.gymId === gymId))
      ?.items.find((gym) => gym.gymId === gymId)
  );
  const [offset, setOffset] = useState(0);
  const [startingDate, setStartingDate] = useState<Date>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  );
  const [endingDate, setEndingDate] = useState<Date>(new Date());
  const [refresh, setRefresh] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [totalActivityCount, setTotalActivityCount] = useState(0);
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    const fetchActivities = async () => {
      setIsActivityLoading(true);

      try {
        const response = (await API.graphql({
          query: getActivityForSiteLocationMember,
          variables: {
            userId,
            siteLocationId: gymId,
            memberId,
            pageSize: accessLogPageSize,
            offset,
            dateFrom: `${dayjs(startingDate).format('YYYY-MM-DD')} 00:00:00`,
            dateTo: `${dayjs(endingDate).format('YYYY-MM-DD')} 23:59:59`,
            stageId: accessDoorStageId,
            eventType: allEventTypes,
            platformAdmin: userRole === UserRole.SYSTEM_ADMIN
          }
        })) as {
          data: { getActivityForSiteLocationMember: GymActivityResponse };
        };
        const activityResponse = response.data.getActivityForSiteLocationMember;

        setActivities(parseActivities(activityResponse.items, currentGym?.timeZone));
        setTotalActivityCount(activityResponse.totalRecords?.totalRecords || 0);
      } catch (error) {
        console.error('Unable to load the member access log', error);
        setActivities([]);
        setTotalActivityCount(0);
      } finally {
        setIsActivityLoading(false);
      }
    };

    fetchActivities();
  }, [
    currentGym?.timeZone,
    endingDate,
    gymId,
    memberId,
    offset,
    refresh,
    startingDate,
    userId,
    userRole
  ]);

  return (
    <div className="mt30 mr15">
      <AccessLog
        gymId={gymId}
        activities={activities}
        isLoading={isActivityLoading}
        totalActivityCount={totalActivityCount}
        offset={offset}
        startingDate={startingDate}
        endingDate={endingDate}
        onOffsetChange={setOffset}
        onStartingDateChange={setStartingDate}
        onEndingDateChange={setEndingDate}
        onRefresh={() => setRefresh((currentRefresh) => !currentRefresh)}
        showDoorFilter={false}
      />
    </div>
  );
};

export interface MemberDetailsActivityProps {
  memberId: number;
  gymId: number;
}

export default MemberDetailsActivity;
